import { createClient } from "@/prismicio";
import { ArticleDocument } from "@/prismicio-types";
import type { Query } from "@prismicio/client";
import * as prismic from "@prismicio/client";

interface GetProfessionalArticlesParams {
    slug: string;
    limit?: number;
    page?: number;
    sortOrder?: string;
}

export default async function getProfessionalArticles({
    slug,
    limit = 13,
    page = 1,
    sortOrder = "latest",
}: GetProfessionalArticlesParams): Promise<{ articles: Query<ArticleDocument> | null }> {
    const emptyQuery: Query<ArticleDocument> = {
        results: [],
        page: 1,
        results_per_page: limit,
        results_size: 0,
        total_results_size: 0,
        total_pages: 0,
        next_page: null,
        prev_page: null,
    };

    if (!slug) {
        return { articles: emptyQuery };
    }

    const client = createClient();

    try {
        // In Prismic, author documents have a unique UID field.
        // We look up the author document by UID (slug) to retrieve its internal document ID.
        const author = await client.getByUID("author", slug);

        if (!author?.id) {
            return { articles: emptyQuery };
        }

        // Query articles where the Content Relationship 'author' field matches the author document's ID
        // @ts-expect-error Query options configuration
        const queryOptions: prismic.BuildQueryURLArgs = {
            page,
            pageSize: limit,
            filters: [
                prismic.filter.at("my.article.author", author.id),
            ],
            fetchLinks: [
                "author.name",
                "author.bio",
                "author.profile_picture",
                "author.image",
                "category.name",
                "category.category_name",
            ],
            orderings: [
                {
                    field: "document.first_publication_date",
                    direction: sortOrder === "latest" ? "desc" : "asc",
                },
            ],
        };

        const articles = await client.getByType("article", queryOptions);
        return { articles };
    } catch (error) {
        return { articles: emptyQuery };
    }
}
